import * as React from "react";
import { toast } from "@grandvisionhq/common";
import { DiscountCodeForm } from "@grandvisionhq/forms";
import { useMutation } from "@grandvisionhq/graphql";
import { useFeatureToggles, useIntl } from "@grandvisionhq/state";
import { useDispatchTrackEvent } from "@grandvisionhq/svc-tealium/events";
import { isNil } from "@grandvisionhq/utils";
import { getUnappliedVoucherCodes } from "../../../../../../utils/utils/get-unapplied-voucher-codes.js";
import { ADD_CART_VOUCHER_CODES } from "../../../../../../operations/mutations/operations/mutations/add-cart-voucher-codes.js";
import { GET_CART } from "../../../../../../operations/queries/operations/queries/get-cart.js";
const VoucherCodeFormContainer = ({
  inputId,
  className,
  "data-t": dataT,
  cartId
}) => {
  const { isOmniVoucherEnabled } = useFeatureToggles();
  const { getLabel } = useIntl();
  const voucherCodeInputRef = React.useRef("");
  const dispatchTrackEvent = useDispatchTrackEvent();
  const [shouldClearDiscountCode, setShouldClearDiscountCode] = React.useState(false);
  const handleError = React.useCallback((e) => {
    console.error("Voucher error", e);
    toast.failed("common.error.network");
  }, []);
  const handleVoucherSuccessTealium = React.useCallback(
    (discountCode, discountName) => {
      dispatchTrackEvent(
        () => Promise.resolve({
          id: "Event",
          Events_DiscountApplied: "1",
          Order_DiscountName: discountName,
          Order_DiscountCode: discountCode
        })
      );
    },
    [dispatchTrackEvent]
  );
  const handleTentativeVoucherSuccessTealium = React.useCallback(
    (discountCode, errorMessage) => {
      dispatchTrackEvent(
        () => Promise.resolve({
          id: "Event",
          Events_DiscountTentative: "1",
          Error_Source: "User",
          Error_Code: `PromoCode - ${errorMessage}`,
          Order_DiscountCode: discountCode
        })
      );
    },
    [dispatchTrackEvent]
  );
  const [addCartVoucherCodes] = useMutation(
    ADD_CART_VOUCHER_CODES,
    {
      onError: handleError,
      onCompleted(data) {
        const newCart = data?.addCartVoucherCodes.cart;
        const voucherStates = newCart?.vouchers?.map(({ state }) => state);
        if (voucherStates?.some((state) => state != "MatchesCart")) {
          newCart?.vouchers?.forEach(({ state, code }) => {
            if (state !== "MatchesCart") {
              if (voucherCodeInputRef.current) {
                handleTentativeVoucherSuccessTealium(
                  code,
                  getLabel(`cart.validations.discountcode.${state.toLowerCase()}`)
                );
              }
              toast.failed(`cart.validations.discountcode.${state.toLowerCase()}`);
            }
          });
          return;
        }
        if (newCart?.vouchers?.length) {
          const unappliedVouchers = getUnappliedVoucherCodes(
            newCart?.vouchers,
            newCart?.appliedDiscounts
          );
          const currentVoucherInApplied = unappliedVouchers?.find(
            (voucher) => voucher.code == voucherCodeInputRef.current
          );
          if (currentVoucherInApplied) {
            handleVoucherSuccessTealium(currentVoucherInApplied.code, currentVoucherInApplied.name);
          }
          if (unappliedVouchers?.length) {
            toast.failed(`cart.validations.discountcode.doesnotmatchcart`);
            return;
          }
        }
        if (data?.addCartVoucherCodes.failures?.length) {
          for (const failure of data.addCartVoucherCodes.failures) {
            if (failure.__typename === "VoucherCodeNotApplicableFailure") {
              handleTentativeVoucherSuccessTealium(
                voucherCodeInputRef.current,
                getLabel("cart.validations.discountcode.notvalid")
              );
              toast.failed("cart.validations.discountcode.notvalid");
              return;
            } else if (failure.__typename === "UnknownCartMutationFailure") {
              handleTentativeVoucherSuccessTealium(
                voucherCodeInputRef.current,
                getLabel("common.error.network")
              );
              toast.failed("common.error.network");
              return;
            }
          }
        }
        const appliedVoucher = newCart?.vouchers?.find(
          (voucher) => voucher.code === voucherCodeInputRef.current
        );
        if (appliedVoucher) {
          handleVoucherSuccessTealium(appliedVoucher.code, appliedVoucher.name);
        }
        toast.success("cart.validations.discountcode.matchescart");
        setShouldClearDiscountCode(true);
      }
    }
  );
  const handleSubmit = React.useCallback(
    (inputValue) => {
      setShouldClearDiscountCode(false);
      if (!cartId) {
        return;
      }
      voucherCodeInputRef.current = inputValue;
      addCartVoucherCodes({
        variables: {
          cartInput: {
            cartId,
            voucherCodes: [inputValue]
          }
        },
        update(cache, { data }) {
          if (data && data.addCartVoucherCodes.success === true) {
            const newCart = data.addCartVoucherCodes.cart;
            cache.writeQuery({
              query: GET_CART,
              variables: { id: cartId },
              data: { cart: { cart: newCart, validationErrors: null, failures: null } }
            });
          }
        }
      });
    },
    [cartId, addCartVoucherCodes]
  );
  if (isNil(cartId)) {
    return null;
  }
  return /* @__PURE__ */ React.createElement(
    DiscountCodeForm,
    {
      content: {
        inputField: {
          inputId: inputId || "discount-code",
          type: "text"
        }
      },
      className,
      onSubmit: handleSubmit,
      placeholderLabel: getLabel("cart.discountForm.placeholder"),
      buttonCaptionLabel: getLabel("cart.discountForm.cta"),
      "data-t": dataT,
      showVoucherScanner: isOmniVoucherEnabled,
      shouldClearDiscountCode
    }
  );
};
export {
  VoucherCodeFormContainer
};
//# sourceMappingURL=VoucherCodeFormContainer.js.map
